import React from 'react';
import type { Project, Shoot } from '../../types/portal';
import { isoOf, shootDates } from '../../utils/projectShoots';
import type { MirroredEvent } from '../../utils/calendarMirror';
import { entryDates, type CalendarEntry } from '../../utils/calendarEntries';
import { CalendarDay } from './CalendarDay';

/** Monday first — the UAE working week runs Monday to Friday. */
export const WEEKDAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
export interface Entry {
  shoot: Shoot;
  projectId: string;
  project?: Project;
}

/** The Monday on or before the 1st, so every month starts on a full week. */
export function gridStart(month: Date): Date {
  const first = new Date(month.getFullYear(), month.getMonth(), 1);
  const weekday = (first.getDay() + 6) % 7; // 0 = Monday
  first.setDate(first.getDate() - weekday);
  return first;
}

/** Six weeks, always — a grid that changes height as you page through months
    makes the controls above it jump under the cursor. */
export function monthGrid(month: Date): Date[] {
  const start = gridStart(month);
  return Array.from({
    length: 42
  }, (_, index) => {
    const day = new Date(start);
    day.setDate(start.getDate() + index);
    return day;
  });
}

/** ISO date → the shoots covering it, a run appearing on each of its days. */
export function byDate(entries: Entry[]): Map<string, Entry[]> {
  const map = new Map<string, Entry[]>();
  for (const entry of entries) {
    for (const iso of shootDates(entry.shoot)) {
      const list = map.get(iso) ?? [];
      list.push(entry);
      map.set(iso, list);
    }
  }
  return map;
}

/** The same for Portal OS's own diary entries. */
function entriesByDate(entries: CalendarEntry[]): Map<string, CalendarEntry[]> {
  const map = new Map<string, CalendarEntry[]>();
  for (const entry of entries) {
    for (const iso of entryDates(entry)) {
      const list = map.get(iso) ?? [];
      list.push(entry);
      map.set(iso, list);
    }
  }
  for (const [iso, list] of map) {
    map.set(iso, [...list].sort((a, b) => (a.startTime || '99').localeCompare(b.startTime || '99')));
  }
  return map;
}

/** And for mirrored Google entries. Standing commitments — the weekly radio
    slot, the internal meeting, travel — sort to the bottom of the day, so
    what is actually happening reads first. */
function eventsByDate(events: MirroredEvent[]): Map<string, MirroredEvent[]> {
  const map = new Map<string, MirroredEvent[]>();
  for (const event of events) {
    const cursor = new Date(`${event.startDate}T00:00:00`);
    const last = new Date(`${event.endDate}T00:00:00`);
    if (Number.isNaN(cursor.getTime()) || Number.isNaN(last.getTime())) continue;
    let guard = 0;
    while (cursor <= last && guard < 100) {
      const iso = isoOf(cursor);
      const list = map.get(iso) ?? [];
      list.push(event);
      map.set(iso, list);
      cursor.setDate(cursor.getDate() + 1);
      guard += 1;
    }
  }
  for (const [iso, list] of map) {
    map.set(iso, [...list].sort((a, b) => Number(a.notProduction) - Number(b.notProduction)));
  }
  return map;
}
interface Props {
  month: Date;
  entries: Entry[];
  /** Portal OS's own diary entries — editable here, and pushed to Google. */
  notes: CalendarEntry[];
  /** Company-calendar entries, already stripped of the ones that ARE a shoot
      or one of our own entries, so nothing is drawn twice. */
  events: MirroredEvent[];
  selectedId: string;
  selectedNoteId: string;
  /** The day this page was opened ON, from the Week Ahead. Marked until the
      first interaction, then cleared by the page above. Not a selection: it
      holds nothing open and nothing is waiting on it. */
  focusDate?: string;
  onSelect: (entry: Entry) => void;
  onSelectNote: (entry: CalendarEntry) => void;
  onAdd: (iso: string) => void;
}
export function CalendarMonth({
  month,
  entries,
  notes,
  events,
  selectedId,
  selectedNoteId,
  focusDate = '',
  onSelect,
  onSelectNote,
  onAdd
}: Props) {
  const days = monthGrid(month);
  const shootsOn = byDate(entries);
  const notesOn = entriesByDate(notes);
  const eventsOn = eventsByDate(events);
  const today = isoOf(new Date());
  return <div className="mt-8 border-t border-l border-ruleDark" data-id="element-540" data-mp="2wezrq47bjtt4">
      <div className="grid grid-cols-7" data-id="element-541" data-mp="31dje25ptr7m0">
        {WEEKDAYS.map(day => <div key={day} className="border-b border-r border-ruleDark px-2 py-3 sm:px-3" data-id="element-542" data-mp="36bkxwsv2rayj">
            <span className="label-sm text-paper/35" data-id="element-543" data-mp="28o2pxk98mcvl">{day}</span>
          </div>)}

        {days.map(day => {
        const iso = isoOf(day);
        return <CalendarDay key={iso} iso={iso} dayNumber={day.getDate()} inMonth={day.getMonth() === month.getMonth()} isToday={iso === today} isFocused={focusDate !== '' && iso === focusDate} shoots={shootsOn.get(iso) ?? []} entries={notesOn.get(iso) ?? []} events={eventsOn.get(iso) ?? []} selectedShootId={selectedId} selectedEntryId={selectedNoteId} onSelectShoot={onSelect} onSelectEntry={onSelectNote} onAdd={onAdd} data-id="element-544" data-mp="2ilqludikxqfc" />;
      })}
      </div>
    </div>;
}